import type { HeldCatalogueItem, HeldStock } from '@repo/desktop-bridge';
import { PARK_TIME_ZONE } from '@repo/desktop-bridge';

/**
 * What the records say about a shelf, in words a cashier can act on.
 *
 * **The till warns and it never refuses** — CL-801, settled between the two
 * orchestrators during the Sync spec and not this screen's to reopen. The
 * reasoning is worth carrying here rather than leaving in a document: a pushed
 * figure is stale by construction. It is a snapshot at `asOf` and the till has
 * been selling since, from this counter and from every other one at the outlet.
 * A till that hard-refuses a sale on a stale number stops the park trading
 * during exactly the outage the offline design exists to survive, which is a
 * worse failure than overselling by one and is self-inflicted.
 *
 * So nothing in this module returns a boolean anything can be disabled by, and
 * nothing it says is worded as a refusal. It answers "what did the records say,
 * and when" — and the screen puts the age beside it and a way to ask for a fresh
 * one, because a warning nobody can respond to is decoration.
 *
 * ## The three distinctions, and why each is a separate case here
 *
 * They are separate `kind`s rather than one number plus a threshold precisely
 * because collapsing them is the easy mistake:
 *
 * - **`not-stocked` is not `none-left`.** An item the outlet does not stock is
 *   absent from the position entirely. "We have none" and "we do not carry it"
 *   are different sentences to somebody standing at a counter, and the API is
 *   careful to keep them apart — so this must not turn an absence into a zero.
 * - **`behind` is not `none-left` either**, and it is never clamped up to it. A
 *   negative level means the records owe the shelf that many, which is real and
 *   worth surfacing: it is the signal a stocktake exists to catch, and it is
 *   true whatever the basket happens to be asking for.
 * - **An untracked item has no case at all.** The position covers only
 *   stock-tracked items, so an untracked item's absence says nothing about a
 *   shelf, and reading it as an empty one would put a false warning on every
 *   postcard in the shop.
 */

/** What the records say, where they say anything worth a cashier's attention. */
export type StockNote =
  /** Tracked, and this outlet does not stock it — a different fact from zero. */
  | { readonly kind: 'not-stocked' }
  /**
   * The records show fewer than none. They disagree with the shelf, which is a
   * real state and never one to hide.
   */
  | { readonly kind: 'behind'; readonly level: number }
  /** Stocked here, and the records say the shelf is empty. */
  | { readonly kind: 'none-left' }
  /** Stocked here, and the records show fewer than the basket is asking for. */
  | { readonly kind: 'short'; readonly level: number };

/**
 * The slice of a catalogue item this needs.
 *
 * `HeldCatalogueItem` satisfies it structurally. Narrow on purpose: `tracksStock`
 * is the whole reason the id is not enough, and stating both here means a caller
 * cannot pass an id and quietly lose the distinction.
 */
export type StockedItem = Pick<HeldCatalogueItem, 'id' | 'tracksStock'>;

/**
 * What to say about one basket line, or `null` where there is nothing to say.
 *
 * `null` covers three different silences and they are all correct: this device
 * has never been pushed a position, the item is not stock-tracked, or the
 * records cover what is being sold. None of them is a reason to draw anything.
 */
export function stockNoteFor(
  item: StockedItem,
  wanted: number,
  held: HeldStock | null,
): StockNote | null {
  // Nothing pushed yet. The screen says that once, plainly, rather than putting
  // the same sentence beside every line.
  if (held === null) return null;

  // Untracked items never appear in a position at any level, so their absence is
  // not evidence of anything. This check must come before the lookup below or
  // every one of them would read as `not-stocked`.
  if (!item.tracksStock) return null;

  const level = held.levels.find((row) => row.catalogueItemId === item.id)?.level;

  if (level === undefined) return { kind: 'not-stocked' };

  // Before the comparison with `wanted`, deliberately: the records disagreeing
  // with the shelf is worth saying whether or not this basket runs into it.
  if (level < 0) return { kind: 'behind', level };

  if (level === 0) return { kind: 'none-left' };

  return level < wanted ? { kind: 'short', level } : null;
}

/**
 * The note as a sentence.
 *
 * Every one of them says *the records* rather than stating a fact about the
 * shelf, because that is the honest voice for a figure the till was handed some
 * time ago and cannot check. None of them tells anybody they cannot sell, and
 * none may ever be reworded to.
 */
export function describeStockNote(note: StockNote): string {
  switch (note.kind) {
    case 'not-stocked':
      return 'The records do not have this among the stock kept here.';

    case 'behind':
      // The number is said rather than softened. −2 means the records owe the
      // shelf two, and rounding that up to "none" throws away the one figure
      // somebody would act on.
      return `The records show ${String(note.level)} here, so they and the shelf disagree.`;

    case 'none-left':
      return 'The records show none of this left.';

    case 'short':
      return `The records show only ${String(note.level)} of this.`;
  }
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * How old the position is, in the largest two units that describe it.
 *
 * **The point of the whole feature is that this is never hidden.** A level
 * without an age implies "now", which the figure has no way to be — so the
 * screen states the age beside every number it draws from the position.
 *
 * The age arrives already computed on the device's **effective** clock, so
 * nothing here reads a clock (`lib/clock.ts` governs that, and this file asks
 * nothing about the current time). A negative age means the machine's clock ran
 * backwards behind the device clock's high-water mark; it reads as no age rather
 * than as a figure from the future.
 */
export function describeStockAge(ageMs: number): string {
  if (ageMs < MINUTE_MS) return 'just now';

  if (ageMs >= DAY_MS) {
    const days = Math.floor(ageMs / DAY_MS);
    const hours = Math.floor((ageMs % DAY_MS) / HOUR_MS);

    return hours === 0 ? `${String(days)}d ago` : `${String(days)}d ${String(hours)}h ago`;
  }

  if (ageMs >= HOUR_MS) {
    const hours = Math.floor(ageMs / HOUR_MS);
    const minutes = Math.floor((ageMs % HOUR_MS) / MINUTE_MS);

    return minutes === 0 ? `${String(hours)}h ago` : `${String(hours)}h ${String(minutes)}m ago`;
  }

  return `${String(Math.floor(ageMs / MINUTE_MS))}m ago`;
}

/**
 * The park's own clock, for the instant a position is as at.
 *
 * In Munnar's zone, never the machine's: the tills ship on US Pacific (#331),
 * and an instant rendered anywhere else has the cashier doing arithmetic to work
 * out whether the figure is from before or after the coach party.
 *
 * Time only, and no date. A position more than a day old is vanishingly rare and
 * the age beside this says so when it happens; a date on every till screen every
 * day would be four characters of noise for the case that does not occur.
 */
const AS_AT = new Intl.DateTimeFormat('en-IN', { timeZone: PARK_TIME_ZONE, timeStyle: 'short' });

/** `new Date(iso)` here parses what the **server** sent. It reads no clock. */
export const formatStockAsOf = (asOf: string): string => AS_AT.format(new Date(asOf));
